import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import { http } from '@/helpers/http'
import { useUserStore } from '@/stores/user'
import { checkWgUrlAccess } from '@/helpers/wgUrlAccess'
import { track } from '@/helpers/mixpanelDes'
import type { THomeChatResponse } from '@/types/claude'
import type { TOutputType, TWgProMonthlyBooksInfoResponse } from '@/types/wordgenie-pro-state'

const OUT_OF_CREDITS_MESSAGE =
  'Oops! It looks like you have run out of credits. Please contact support for more free credits.'

export const useHomeChatStore = defineStore('home-chat', () => {
  const router = useRouter()
  const user = useUserStore()

  const input = ref('')
  const loading = ref(false)
  const errorMessage = ref('')
  const canUpgrade = ref(false)
  const forcedType = ref<TOutputType | null>(null)
  const monthlyBooks = ref<TWgProMonthlyBooksInfoResponse | null>(null)

  async function fetchMonthlyBooksInfo() {
    try {
      monthlyBooks.value = await http.get<TWgProMonthlyBooksInfoResponse>('/wordgenie-pro/monthly-books-info')
    } catch {
      monthlyBooks.value = null
    }

    // test usage warning (>= 80%)
    // monthlyBooks.value = { result: true, used: 8, limit: 10, plan_name: 'Standard', unlimited: false }

    // test usage blocked (100%)
    // monthlyBooks.value = { result: true, used: 10, limit: 10, plan_name: 'Standard', unlimited: false }
  }

  function clearError() {
    errorMessage.value = ''
    canUpgrade.value = false
  }

  async function send() {
    const text = input.value.trim()
    if (!text || loading.value) return

    clearError()

    if (forcedType.value) {
      track('homechat-forced', { type: forcedType.value, query: text })
      router.push(`/wordgenie-pro/${forcedType.value}?prompt=${encodeURIComponent(text)}`)
      return
    }

    loading.value = true

    try {
      const res = await http.post<THomeChatResponse>('/wordgenie-pro/home-chat', { message: text })

      if (!res.result && res.message === 'NOT_ENOUGH_CREDITS') {
        errorMessage.value = OUT_OF_CREDITS_MESSAGE
        return
      }

      if (!res.result) {
        errorMessage.value = res.message || 'Something went wrong. Please try again.'
        return
      }

      if (res.action === 'navigate' && res.url) {
        const access = checkWgUrlAccess(res.url, user.user, user.accountType)

        if (!access.allowed) {
          errorMessage.value = access.reason
          canUpgrade.value = access.kind === 'no_access'
          return
        }

        if (!res.url.startsWith('/wordgenie-pro')) {
          track('homechat-redirect', { target: res.url, query: text })
        }
        router.push(res.url)
        return
      }

      if (res.action === 'generate_book') {
        router.push(`/wordgenie-pro?prompt=${encodeURIComponent(text)}`)
        return
      }

      if (res.action === 'help') {
        window.Intercom?.('show')
        return
      }

      if (res.action === 'chat') {
        router.push(`/wordgenie-pro?prompt=${encodeURIComponent(text)}`)
      }
    } catch {
      errorMessage.value = 'Something went wrong. Please try again.'
    } finally {
      loading.value = false
    }
  }

  return {
    input,
    loading,
    errorMessage,
    canUpgrade,
    forcedType,
    monthlyBooks,
    fetchMonthlyBooksInfo,
    send,
    clearError,
  }
})
